/**
 * Folk & Shepherd — configurator v2 frame engine.
 *
 * Rebuilds a framed print of any size and orientation from 4 side pieces
 * per finish (see assets/frame-manifest.json), mitred to 45° at the
 * corners like a real frame — not corner tiles — plus plain DOM/CSS for
 * the mount bevel, shadows and glass. No canvas, no WebGL. See Desktop/
 * Fix mitred frame corners.md for why (corner tiles broke the grain at
 * every join) and the exact approach.
 *
 * Geometry and shadow/bevel values are ported from
 * tools/frame-renderer/compose.py (the Python reference) — see the
 * comments by each constant for where a number comes from. All geometry
 * is computed in millimetres and converted to display pixels last, so
 * the frame and mount keep their true relative width at every size.
 *
 * Usage:
 *   FolkFrameEngine.render(containerEl, {
 *     config,       // parsed frame-engine-config.json
 *     frameValue,   // option value, e.g. "Box Frame - Oak", or "No Frame"
 *     mountValue,   // option value, e.g. "White Mount", or "No Mount" / null
 *     sizeValue,    // option value, e.g. '18×12"' — exact key in config.sizes
 *     orientation,  // "landscape" | "portrait"
 *     artworkUrl,   // image src, shown with object-fit: contain
 *     assetUrl,     // (assetsKey, piece) => url, e.g. "box-oak" + "side-top"
 *     stagePx,      // optional, defaults to 1000
 *     printSpecs,   // optional: size from config.sizes[...] per-size specs (see computeGeometry)
 *   });
 *
 * Returns the computed geometry object, useful for debugging and for the
 * "Framed size approx." line (phase 3).
 */
(function (global) {
  'use strict';

  var INCH_MM = 25.4;

  // Bevel facet colours — compose.py's `lit`/`shade` RGBA, light from top-left
  // so the bottom and right facets of the 45° cut catch the light.
  var BEVEL_SHADE = 'rgb(214, 210, 202)'; // top + left facets
  var BEVEL_LIT = 'rgb(250, 249, 245)'; // bottom + right facets

  // Paper the artwork sits on when object-fit: contain leaves a gap.
  var PAPER_WHITE = '#ffffff';

  // Wall drop shadow — two layers, the way real cast shadows actually read
  // (and the usual trick for a convincing CSS shadow: a single soft blur
  // looks flat, a tight "contact" shadow plus a larger, softer "ambient"
  // one looks like a real object lifted slightly off a wall). Started
  // from compose.py's on_stage() single-layer shadow (offset 7,9 blur 9 @
  // 1400px reference) and split/extended it; no longer a strict port.
  // Same warm wall-shadow brown throughout, just lighter in the ambient
  // layer.
  var WALL_SHADOW_REF_PX = 1400;
  var WALL_SHADOW_CONTACT = { dx: 5, dy: 8, blur: 10, color: 'rgba(40, 32, 24, 0.30)' };
  var WALL_SHADOW_AMBIENT = { dx: 0, dy: 24, blur: 42, color: 'rgba(40, 32, 24, 0.14)' };

  // Shadow the mount's inner bevel casts onto the print — compose.py's
  // 1.2mm band at alpha 70/255, blurred by (asset ppm * 0.8) asset-px,
  // which is 0.8mm once the asset's own px-per-mm cancels out.
  var MOUNT_EDGE_SHADOW_MM = 1.2;
  var MOUNT_EDGE_SHADOW_ALPHA = 0.275;
  var MOUNT_EDGE_BLUR_MM = 0.8;

  // Shadow the frame casts inward onto mount/print — compose.py's
  // depth_shadow_mm split into a strong top/left band (half the value, alpha
  // 120/255) and a faint bottom/right band (a fifth of the value, alpha
  // 40/255), blurred by depth_shadow_mm / 2.5.
  var INNER_SHADOW_STRONG_FRACTION = 1 / 2;
  var INNER_SHADOW_FAINT_FRACTION = 1 / 5;
  var INNER_SHADOW_BLUR_FRACTION = 1 / 2.5;
  var INNER_SHADOW_STRONG_ALPHA = 0.47;
  var INNER_SHADOW_FAINT_ALPHA = 0.157;

  /**
   * All geometry in mm, then in display px for the given stage size.
   * Every box the renderer draws is listed here, outermost first:
   *
   *   outer   the whole piece (frame outside edge; the print, unframed)
   *   open    the frame's visible opening (outer minus the frame face)
   *   window  where the artwork shows: the mount window, or the opening
   *   art     the artwork image box, centred in the window and clipped by
   *           it — bigger than the window where a frame lip covers the
   *           print's edge
   *
   * Two sizing models:
   *
   *  - opts.printSpecs (the print configurator): per-size numbers from
   *    config.sizes[...] — print_cm, and for families that have them
   *    (Prodigi box) the glass/window/outside sizes of the actual product,
   *    which uses a bigger frame when mounted. Families without per-size
   *    numbers (SIM thin) are sized from the print: frame size = print
   *    size, outside = print + border each side, never mounted. The stage
   *    scale is set by the biggest piece any option makes at this size,
   *    so a mounted frame really does look bigger than an unmounted one.
   *
   *  - otherwise (the canvas configurator), the original model: inches +
   *    family border (+ uniform mount_mm), always scaled to fill the stage.
   */
  function computeGeometry(config, frameValue, mountValue, sizeValue, orientation, stagePx, printSpecs) {
    var sizeCfg = config.sizes[sizeValue];
    if (!sizeCfg) {
      throw new Error('FolkFrameEngine: unknown size "' + sizeValue + '"');
    }
    var frameCfg = config.frames[frameValue]; // undefined for "No Frame"
    var framed = !!frameCfg;
    var familyKey = framed ? frameCfg.family : null;
    var familyCfg = framed ? config.families[familyKey] : null;
    var mountCfg = framed && mountValue ? config.mounts[mountValue] : null;
    var portrait = orientation === 'portrait';
    var useSpecs = !!printSpecs && !!sizeCfg.print_cm;

    // [long, short] pair -> {w, h} for this orientation.
    function orient(longV, shortV) {
      return portrait ? { w: shortV, h: longV } : { w: longV, h: shortV };
    }
    function orientCm(pair) { return orient(pair[0] * 10, pair[1] * 10); }
    function inset(box, by) { return { w: box.w - 2 * by, h: box.h - 2 * by }; }

    var sizeIn = orient(Math.max(sizeCfg.w_in, sizeCfg.h_in), Math.min(sizeCfg.w_in, sizeCfg.h_in));
    var borderMm = framed ? familyCfg.border_mm : 0;
    var mounted = false;
    var print, glass, outer, open, win, art, refLongMm, artFit;

    if (useSpecs) {
      print = orientCm(sizeCfg.print_cm);
      var spec = framed ? sizeCfg[familyKey] : null;
      var mountedSpec = framed ? sizeCfg[familyKey + '_mounted'] : null;
      mounted = !!mountCfg && familyCfg.mountable !== false && !!mountedSpec;
      artFit = 'cover';

      if (!framed) {
        glass = outer = open = win = art = print;
      } else if (spec) {
        var s = mounted ? mountedSpec : spec;
        glass = orientCm(s.glass_cm);
        outer = orientCm(s.outside_cm);
        open = inset(outer, borderMm);
        win = mounted ? orientCm(s.window_cm) : open;
        // Mounted: the art fills the mount window. Unmounted: the print is
        // the glass size, its edge tucked under the frame lip.
        art = mounted ? win : glass;
      } else {
        glass = open = win = art = print;
        outer = inset(print, -borderMm);
      }

      // Scale to the biggest piece any frame option makes at this size.
      refLongMm = Math.max(outer.w, outer.h);
      Object.keys(config.families).forEach(function (k) {
        [sizeCfg[k], sizeCfg[k + '_mounted']].forEach(function (sp) {
          if (sp && sp.outside_cm) refLongMm = Math.max(refLongMm, sp.outside_cm[0] * 10);
        });
        if (!sizeCfg[k]) refLongMm = Math.max(refLongMm, sizeCfg.print_cm[0] * 10 + 2 * (config.families[k].border_mm || 0));
      });
    } else {
      mounted = !!mountCfg && familyCfg.mountable !== false && !!familyCfg.mount_mm;
      var mountMm = mounted ? familyCfg.mount_mm : 0;
      glass = print = { w: sizeIn.w * INCH_MM, h: sizeIn.h * INCH_MM };
      outer = inset(glass, -borderMm);
      open = glass;
      win = art = inset(glass, mountMm);
      artFit = 'contain';
      refLongMm = Math.max(outer.w, outer.h);
    }

    var outerLongMm = Math.max(outer.w, outer.h);
    var ppmDisplay = (stagePx * config.stage.fill * sizeCfg.display_scale) / refLongMm;
    var innerShadowMm = framed ? familyCfg.inner_shadow_mm : 0;

    var mm = {
      sizeInW: sizeIn.w, sizeInH: sizeIn.h,
      printW: print.w, printH: print.h,
      glassW: glass.w, glassH: glass.h,
      outerW: outer.w, outerH: outer.h, outerLong: outerLongMm,
      border: borderMm,
      openW: open.w, openH: open.h,
      windowW: win.w, windowH: win.h,
      artW: art.w, artH: art.h,
      board: config.board_mm, innerShadow: innerShadowMm
    };
    var px = {};
    ['printW', 'printH', 'outerW', 'outerH', 'border', 'openW', 'openH', 'windowW', 'windowH', 'artW', 'artH', 'board', 'innerShadow']
      .forEach(function (k) { px[k] = mm[k] * ppmDisplay; });

    return {
      framed: framed,
      mounted: mounted,
      familyKey: familyKey,
      assetsKey: framed ? frameCfg.assets : null,
      mountColour: mounted ? mountCfg.colour : null,
      artFit: artFit,
      ppmDisplay: ppmDisplay,
      mm: mm,
      px: px
    };
  }

  function absFill(el, left, top, w, h) {
    el.style.position = 'absolute';
    el.style.left = Math.round(left) + 'px';
    el.style.top = Math.round(top) + 'px';
    el.style.width = Math.round(w) + 'px';
    el.style.height = Math.round(h) + 'px';
  }

  // mountX/mountY: the mount's width beside / above the window — not
  // always equal, since the window size comes from the product spec.
  function addBevel(mountEl, openW, openH, mountX, mountY, boardPx) {
    openW = Math.round(openW); openH = Math.round(openH);
    mountX = Math.round(mountX); mountY = Math.round(mountY); boardPx = Math.max(1, Math.round(boardPx));
    var x0 = mountX - boardPx, y0 = mountY - boardPx;
    var x1 = openW - mountX + boardPx, y1 = openH - mountY + boardPx;

    function facet(points, colour) {
      var div = document.createElement('div');
      div.className = 'ffe-bevel';
      absFill(div, 0, 0, openW, openH);
      div.style.clipPath = 'polygon(' + points.map(function (p) {
        return p[0] + 'px ' + p[1] + 'px';
      }).join(', ') + ')';
      div.style.background = colour;
      mountEl.appendChild(div);
    }

    facet([[x0, y0], [x1, y0], [x1 - boardPx, y0 + boardPx], [x0 + boardPx, y0 + boardPx]], BEVEL_SHADE); // top
    facet([[x0, y0], [x0 + boardPx, y0 + boardPx], [x0 + boardPx, y1 - boardPx], [x0, y1]], BEVEL_SHADE); // left
    facet([[x0, y1], [x0 + boardPx, y1 - boardPx], [x1 - boardPx, y1 - boardPx], [x1, y1]], BEVEL_LIT); // bottom
    facet([[x1, y0], [x1, y1], [x1 - boardPx, y1 - boardPx], [x1 - boardPx, y0 + boardPx]], BEVEL_LIT); // right
  }

  function addMountEdgeShadow(windowEl, ppmDisplay) {
    var t = MOUNT_EDGE_SHADOW_MM * ppmDisplay;
    var b = MOUNT_EDGE_BLUR_MM * ppmDisplay;
    var s = -b * 0.5;
    var c = 'rgba(0, 0, 0, ' + MOUNT_EDGE_SHADOW_ALPHA + ')';
    windowEl.style.boxShadow = [
      'inset 0 ' + t + 'px ' + b + 'px ' + s + 'px ' + c,
      'inset ' + t + 'px 0 ' + b + 'px ' + s + 'px ' + c
    ].join(', ');
  }

  function addInnerShadow(el, innerShadowMm, ppmDisplay) {
    if (!innerShadowMm) return;
    var strongPx = innerShadowMm * INNER_SHADOW_STRONG_FRACTION * ppmDisplay;
    var faintPx = innerShadowMm * INNER_SHADOW_FAINT_FRACTION * ppmDisplay;
    var blurPx = innerShadowMm * INNER_SHADOW_BLUR_FRACTION * ppmDisplay;
    var spread = -blurPx * 0.4;
    var strong = 'rgba(0, 0, 0, ' + INNER_SHADOW_STRONG_ALPHA + ')';
    var faint = 'rgba(0, 0, 0, ' + INNER_SHADOW_FAINT_ALPHA + ')';
    var shadows = [
      'inset 0 ' + strongPx + 'px ' + blurPx + 'px ' + spread + 'px ' + strong,
      'inset ' + strongPx + 'px 0 ' + blurPx + 'px ' + spread + 'px ' + strong,
      'inset 0 -' + faintPx + 'px ' + blurPx + 'px ' + spread + 'px ' + faint,
      'inset -' + faintPx + 'px 0 ' + blurPx + 'px ' + spread + 'px ' + faint
    ];
    el.style.boxShadow = el.style.boxShadow ? el.style.boxShadow + ', ' + shadows.join(', ') : shadows.join(', ');
  }

  // Zoom the artwork 1% inside its own box. Where the image edge lands
  // between screen pixels, the white window behind it can show through as
  // a hairline "white bar"; cropping 0.5% off each edge hides that on every
  // version (unframed, box, mounted, thin, canvas). Clipped to the art's
  // own box, so the unframed paper border keeps its width.
  var ARTWORK_ZOOM = 1.01;

  function addArtwork(windowEl, artworkUrl, insetPx, box) {
    // The positioned, clipping box; the image fills it.
    var holder = document.createElement('div');
    holder.className = 'ffe-artwork-box';
    holder.style.overflow = 'hidden';

    var img = document.createElement('img');
    img.className = 'ffe-artwork';
    // Already decoded by preload() — draw it in the same frame as the rest
    // of the stage rather than a beat later (the white-window flash).
    img.decoding = 'sync';
    img.src = artworkUrl;
    img.alt = '';
    img.style.display = 'block';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.transform = 'scale(' + ARTWORK_ZOOM + ')';

    if (insetPx) {
      // No-frame mode: the print itself stays full size, the artwork
      // shrinks inside it so PAPER_WHITE shows through as a plain
      // border — a flat print's own margin, not a mat or frame. insetPx
      // is always 2% of the print's own width, applied as a fixed px
      // gap on all 4 sides (not 2% of each side's own axis), so the
      // border reads the same thickness on every edge regardless of
      // the print's aspect ratio. object-fit: cover (not contain) fills
      // that exact inset box, cropping the artwork as needed — contain
      // would letterbox whenever the artwork's own aspect ratio doesn't
      // exactly match the print's, adding extra white beyond the border
      // on one axis.
      img.style.objectFit = 'cover';
      holder.style.position = 'absolute';
      holder.style.left = insetPx + 'px';
      holder.style.top = insetPx + 'px';
      holder.style.width = 'calc(100% - ' + (2 * insetPx) + 'px)';
      holder.style.height = 'calc(100% - ' + (2 * insetPx) + 'px)';
    } else if (box) {
      // Framed: the art box (geo.px.artW/H) centred in the window and
      // clipped by it — where it's bigger than the window, the frame lip
      // or mount covers the print's edge, exactly as on the real thing.
      img.style.objectFit = box.fit;
      holder.style.position = 'absolute';
      holder.style.width = Math.round(box.w) + 'px';
      holder.style.height = Math.round(box.h) + 'px';
      holder.style.left = Math.round((box.windowW - box.w) / 2) + 'px';
      holder.style.top = Math.round((box.windowH - box.h) / 2) + 'px';
    } else {
      img.style.objectFit = 'contain';
      holder.style.width = '100%';
      holder.style.height = '100%';
    }
    holder.appendChild(img);
    windowEl.appendChild(holder);
  }


  // Mitred join, not corner tiles — see Desktop/Fix mitred frame
  // corners.md. Each side strip runs the frame's full outer length, cut
  // from the middle of the render (never stretched: background-size
  // locks the strip's thickness to the border width, background-position:
  // center crops the length). Left/right sit underneath as plain
  // rectangles; top/bottom sit above, clip-path'd to a trapezoid whose 45°
  // edges meet exactly where left/right's own edges are, so the grain
  // runs unbroken along each length right up to a clean mitred corner —
  // no tile, no seam to misalign. Only left/right would ever show a
  // seam if clipped too (two antialiased diagonals meeting), so only
  // top/bottom are clipped, per the doc's "don't".
  function addFrameSides(piece, assetsKey, assetUrl, outerW, outerH, borderPx, maxSideMm, outerLongMm) {
    outerW = Math.round(outerW); outerH = Math.round(outerH); borderPx = Math.round(borderPx);

    if (maxSideMm && outerLongMm > maxSideMm) {
      console.warn('FolkFrameEngine: outer size ' + Math.round(outerLongMm) + 'mm exceeds ' +
        assetsKey + '\'s rendered max_side_mm (' + maxSideMm + 'mm) — the side strip will be ' +
        'upscaled beyond its native resolution. Re-render with a larger --outer.');
    }

    function side(name, horizontal, sizeCss) {
      var div = document.createElement('div');
      div.className = 'ffe-side ffe-side-' + name;
      div.style.position = 'absolute';
      div.style.backgroundImage = 'url(' + assetUrl(assetsKey, 'side-' + name) + ')';
      div.style.backgroundRepeat = 'no-repeat';
      div.style.backgroundPosition = 'center';
      div.style.backgroundSize = sizeCss;
      piece.appendChild(div);
      return div;
    }

    // Underneath, full height/width, never clipped.
    var left = side('left', false, '100% auto');
    left.style.top = '0'; left.style.bottom = '0'; left.style.left = '0';
    left.style.width = borderPx + 'px';
    left.style.zIndex = '1';

    var right = side('right', false, '100% auto');
    right.style.top = '0'; right.style.bottom = '0'; right.style.right = '0';
    right.style.width = borderPx + 'px';
    right.style.zIndex = '1';

    // On top, mitred to 45° at both ends: full length at the outer edge,
    // inset by borderPx on each side at the inner edge, so the slanted
    // edge exactly traces where left/right's inner edge already sits.
    var top = side('top', true, 'auto 100%');
    top.style.left = '0'; top.style.right = '0'; top.style.top = '0';
    top.style.height = borderPx + 'px';
    top.style.zIndex = '2';
    top.style.clipPath = 'polygon(0 0, 100% 0, calc(100% - ' + borderPx + 'px) 100%, ' + borderPx + 'px 100%)';

    var bottom = side('bottom', true, 'auto 100%');
    bottom.style.left = '0'; bottom.style.right = '0'; bottom.style.bottom = '0';
    bottom.style.height = borderPx + 'px';
    bottom.style.zIndex = '2';
    bottom.style.clipPath = 'polygon(' + borderPx + 'px 0, calc(100% - ' + borderPx + 'px) 0, 100% 100%, 0 100%)';
  }

  function addGlass(host) {
    var div = document.createElement('div');
    div.className = 'ffe-glass';
    absFill(div, 0, 0, host.clientWidth || 0, host.clientHeight || 0);
    div.style.width = '100%';
    div.style.height = '100%';
    div.style.left = '0';
    div.style.top = '0';
    div.style.pointerEvents = 'none';
    div.style.mixBlendMode = 'screen';
    div.style.background = 'linear-gradient(135deg, rgba(255,255,255,.16) 0%, ' +
      'rgba(255,255,255,0) 30%, rgba(255,255,255,0) 68%, rgba(255,255,255,.06) 100%)';
    host.appendChild(div);
  }

  function render(container, opts) {
    var config = opts.config;
    var stagePx = opts.stagePx || 1000;
    var geo = computeGeometry(config, opts.frameValue, opts.mountValue, opts.sizeValue, opts.orientation, stagePx, opts.printSpecs);

    container.innerHTML = '';
    container.classList.add('ffe-stage');
    container.style.position = 'relative';
    container.style.width = stagePx + 'px';
    container.style.height = stagePx + 'px';
    container.style.background = config.stage.background;
    container.style.overflow = 'hidden';

    // Round the outer size and border/mount thicknesses to whole pixels
    // ONCE, here, and derive every nested box from those same integers by
    // plain subtraction. Rounding each box's own left/width independently
    // (the old approach) lets e.g. Math.round(border) + Math.round(outer
    // - 2*border) land 1px short of Math.round(outer) - Math.round(border)
    // — a gap that exposes the white print window past the frame's edge
    // on whichever side the rounding happened to fall short.
    var outerW = Math.round(geo.px.outerW), outerH = Math.round(geo.px.outerH);
    var piece = document.createElement('div');
    piece.className = 'ffe-piece';
    absFill(piece, (stagePx - outerW) / 2, (stagePx - outerH) / 2, outerW, outerH);
    function shadowLayer(s) {
      var k = stagePx / WALL_SHADOW_REF_PX;
      return (s.dx * k) + 'px ' + (s.dy * k) + 'px ' + (s.blur * k) + 'px ' + s.color;
    }
    piece.style.boxShadow = shadowLayer(WALL_SHADOW_CONTACT) + ', ' + shadowLayer(WALL_SHADOW_AMBIENT);
    container.appendChild(piece);

    var borderPx = Math.round(geo.px.border);
    var windowEl, innerHost;

    if (geo.framed) {
      var openW = outerW - 2 * borderPx, openH = outerH - 2 * borderPx;

      var winW = geo.mounted ? Math.round(geo.px.windowW) : openW;
      var winH = geo.mounted ? Math.round(geo.px.windowH) : openH;

      if (geo.mounted) {
        // Window centred in the opening; the mount fills the rest.
        var mountX = Math.round((openW - winW) / 2), mountY = Math.round((openH - winH) / 2);

        var mountEl = document.createElement('div');
        mountEl.className = 'ffe-mount';
        absFill(mountEl, borderPx, borderPx, openW, openH);
        mountEl.style.background = geo.mountColour;
        mountEl.style.overflow = 'hidden';
        piece.appendChild(mountEl);
        innerHost = mountEl;

        addBevel(mountEl, openW, openH, mountX, mountY, geo.px.board);

        windowEl = document.createElement('div');
        windowEl.className = 'ffe-window';
        absFill(windowEl, mountX, mountY, winW, winH);
        windowEl.style.background = PAPER_WHITE;
        windowEl.style.overflow = 'hidden';
        mountEl.appendChild(windowEl);

        addMountEdgeShadow(windowEl, geo.ppmDisplay);
      } else {
        windowEl = document.createElement('div');
        windowEl.className = 'ffe-window';
        absFill(windowEl, borderPx, borderPx, openW, openH);
        windowEl.style.background = PAPER_WHITE;
        windowEl.style.overflow = 'hidden';
        piece.appendChild(windowEl);
        innerHost = windowEl;
      }

      addArtwork(windowEl, opts.artworkUrl, 0, {
        fit: geo.artFit,
        w: geo.mounted ? winW : Math.max(winW, geo.px.artW),
        h: geo.mounted ? winH : Math.max(winH, geo.px.artH),
        windowW: winW, windowH: winH
      });
      addInnerShadow(innerHost, geo.mm.innerShadow, geo.ppmDisplay);
      var familyCfg = config.families[geo.familyKey];
      addFrameSides(piece, geo.assetsKey, opts.assetUrl, outerW, outerH, borderPx,
        familyCfg.max_side_mm, geo.mm.outerLong);
      // Per-family override (e.g. canvas floater has no glass) falls back to
      // the stage-wide default when a family doesn't specify one.
      var wantGlass = familyCfg.glass != null ? familyCfg.glass : config.stage.glass;
      if (wantGlass) addGlass(innerHost);
    } else {
      windowEl = document.createElement('div');
      windowEl.className = 'ffe-window';
      absFill(windowEl, 0, 0, outerW, outerH);
      windowEl.style.background = PAPER_WHITE;
      windowEl.style.overflow = 'hidden';
      piece.appendChild(windowEl);
      addArtwork(windowEl, opts.artworkUrl, geo.px.printW * 0.02);
    }

    return geo;
  }

  // ---- preloading -----------------------------------------------------
  //
  // render() rebuilds the stage from scratch, so switching to a frame whose
  // side textures aren't downloaded yet paints the frame in pieces as each
  // one arrives. preload() fetches and decodes images once, keeping the
  // Image objects referenced so the browser keeps them in memory, and
  // renderWhenReady() leaves the current mockup on screen until everything
  // the next one needs is ready, then swaps it in one go.

  var imageCache = {};

  function preload(urls) {
    return Promise.all((urls || []).filter(Boolean).map(function (url) {
      if (!imageCache[url]) {
        var img = new Image();
        img.decoding = 'async';
        img.src = url;
        var done = img.decode
          ? img.decode()
          : new Promise(function (resolve) { img.onload = img.onerror = resolve; });
        imageCache[url] = { img: img, ready: done.catch(function () {}) }; // a failed image mustn't block the render
      }
      return imageCache[url].ready;
    }));
  }

  // Everything one render() of these opts paints from a URL.
  function urlsFor(opts) {
    var urls = [opts.artworkUrl];
    var frameCfg = opts.config.frames[opts.frameValue];
    if (frameCfg && opts.assetUrl) {
      ['top', 'bottom', 'left', 'right'].forEach(function (side) {
        urls.push(opts.assetUrl(frameCfg.assets, 'side-' + side));
      });
    }
    return urls;
  }

  var READY_TIMEOUT_MS = 2500; // slow network: show what we have rather than nothing new at all

  // Same as render(), but waits (up to READY_TIMEOUT_MS) for this render's
  // images first. If another renderWhenReady() for the same container is
  // called meanwhile, only the latest one draws, so quick clicking through
  // frames never paints a stale choice. Resolves with render()'s geometry,
  // or null if superseded.
  function renderWhenReady(container, opts) {
    var token = (container._ffeRenderToken || 0) + 1;
    container._ffeRenderToken = token;
    var timeout = new Promise(function (resolve) { setTimeout(resolve, READY_TIMEOUT_MS); });
    return Promise.race([preload(urlsFor(opts)), timeout]).then(function () {
      if (container._ffeRenderToken !== token) return null;
      return render(container, opts);
    });
  }

  global.FolkFrameEngine = {
    render: render,
    renderWhenReady: renderWhenReady,
    preload: preload,
    computeGeometry: computeGeometry
  };
})(typeof window !== 'undefined' ? window : this);
